import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MatTableDataSource } from '@angular/material/table';
import { trigger, state, style } from '@angular/animations';

import { DatatableBaseComponent } from '@components/datatable-base.component';
import { AuthService } from '@services/auth.service';
import { UiService } from '@services/ui.service';
import { UsersService } from '@services/users.service';
import { User } from '@classes/user/user';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from '@components/confirm-dialog/confirm-dialog.component';
import { RoleName } from '@enums/role-name.enum';
import { PaginationRequest } from '@classes/pagination/pagination-request';
import { Pagination } from '@classes/pagination/pagination';
import { Sort } from '@angular/material/sort';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { FormControl, FormGroup } from '@angular/forms';

@Component({
  selector: 'app-users',
  templateUrl: './users.component.html',
  styleUrls: ['./users.component.scss'],
  animations: [
    trigger('detailExpand', [
      state(
        'collapsed',
        style({ height: '0px', minHeight: '0', display: 'none' }),
      ),
      state('expanded', style({ height: '*' })),
    ]),
  ],
})
export class UsersComponent extends DatatableBaseComponent implements OnInit {
  private filterSubject = new Subject<string>();

  users: User[];
  filter = '';
  filterForm = new FormGroup({
    includeDeleted: new FormControl(false),
  });
  pagination: Pagination;
  sortValue: string = '';

  displayedColumns: string[] = [
    'username',
    'email',
    'role',
    'clients',
    'sites',
    'createdBy',
    'createdOn',
    'lastLoginOn',
    'actions',
  ];
  dataSource: MatTableDataSource<User>;
  @ViewChild(MatPaginator) paginator: MatPaginator;

  canSendApk: boolean;

  constructor(
    protected uiSrv: UiService,
    private authSrv: AuthService,
    private usersSrv: UsersService,
    private route: ActivatedRoute,
    private dialog: MatDialog,
  ) {
    super(uiSrv);
  }

  ngOnInit() {
    super.ngOnInit();

    this.canSendApk =
      this.authSrv.getUser().role.name !== RoleName.admin_client &&
      this.authSrv.getUser().role.name !== RoleName.client;

    this.initSubscriptions();

    this._initColumnsParams();

    this.readColumnsParamsFromLocalStorage();
    this.generateDisplayedColumns();
  }

  sendWelcomMailDisabled(user: User): boolean {
    return user.hasPassword;
  }

  onSendMail(user: User): void {
    if (user.lastLogonOn != null) {
      return;
    }

    this.isLoading = true;
    this.authSrv.requestPasswordInit(user.username).subscribe(() => {
      this.uiSrv.showSnackbar('users.list.welcomeSent', true);
      this.isLoading = false;
    });
  }

  onSendApkMail(user: User): void {
    this.isLoading = true;
    this.usersSrv.sendApkMail(user.id).subscribe(() => {
      this.uiSrv.showSnackbar('Mail envoyé avec succès', false);
      this.isLoading = false;
    });
  }

  onChangeState(user: User, isActive: boolean): void {
    let state = isActive ? 'activate' : 'deactivate';
    const dialog = this.dialog.open(ConfirmDialogComponent, {
      maxWidth: '500px',
      data: {
        title: 'global.dialog.confirmTitle',
        content: 'users.list.confirm_' + state,
      },
    });
    dialog.afterClosed().subscribe((response) => {
      if (response) {
        this.usersSrv
          .changeStatus(user.id, isActive ? 'activate' : 'deactivate')
          .subscribe(() => {
            this.users.map((u) => {
              if (u.id === user.id) {
                u.isActive = isActive;
              }
            });
          });
      }
    });
  }

  private initTable(): void {
    this.dataSource = new MatTableDataSource(this.users);
    this.dataSource.filterPredicate = (data, filter: string) => {
      const accumulator = (currentTerm, key) => {
        switch (key) {
          case 'role':
            return currentTerm + data.role.description;
          default:
            return currentTerm + data[key];
        }
      };
      const dataStr = Object.keys(data).reduce(accumulator, '').toLowerCase();
      // Transform the filter by converting it to lowercase and removing whitespace.
      const transformedFilter = filter.trim().toLowerCase();
      return dataStr.indexOf(transformedFilter) !== -1;
    };
    this.sortTable();
  }

  private _initColumnsParams() {
    this.localStorageColumnsParamsName = 'ColumnsParamsUsers';

    this.columnsParams = [
      {
        name: 'username',
        title: 'users.list.username',
        editable: true,
        visible: true,
      },
      {
        name: 'email',
        title: 'users.list.email',
        editable: true,
        visible: true,
      },
      {
        name: 'role',
        title: 'users.list.role',
        editable: true,
        visible: true,
      },
      {
        name: 'clients',
        title: 'users.list.clients',
        editable: true,
        visible: true,
      },
      {
        name: 'sites',
        title: 'users.list.sites',
        editable: true,
        visible: true,
      },
      {
        name: 'createdBy',
        title: 'users.list.createdBy',
        editable: true,
        visible: true,
      },
      {
        name: 'createdOn',
        title: 'users.list.createdOn',
        editable: true,
        visible: true,
      },
      {
        name: 'lastLoginOn',
        title: 'users.list.lastLoginOn',
        editable: true,
        visible: true,
      },
      // {
      //   name: 'features',
      //   title: 'users.list.features',
      //   editable: true,
      //   visible: true,
      // },
      {
        name: 'actions',
        title: 'users.list.actions',
        editable: false,
        visible: true,
      },
    ];
  }

  private sortTable(): void {
    this.dataSource.sortingDataAccessor = (item, property) => {
      switch (property) {
        case 'role':
          return item.role.name.toLowerCase();
        case 'clients': {
          if (item.clients.length === 1) {
            return item.clients[0].name.toLowerCase();
          } else {
            return item.clients.map((client) => {
              client.name.toLowerCase();
            });
          }
        }
        case 'sites': {
          if (item.sites.length === 1) {
            return item.sites[0].name.toLowerCase();
          } else {
            return item.sites.map((site) => {
              site.name.toLowerCase();
            });
          }
        }
        default:
          return item[property];
      }
    };

    this.dataSource.sort = this.sort;
  }

  getList(): void {
    const newRequest = new PaginationRequest(
      this.filter,
      this.sortValue ?? '',
      this.pagination.pageNumber,
    );

    const isActive = !this.filterForm.get('includeDeleted').value;

    this.usersSrv.getListWithPagination(newRequest, null, isActive);
  }

  public onFilter(): void {
    this.getList();
  }

  onPageChanged(pageEvent: PageEvent): void {
    this.pagination.pageNumber = pageEvent.pageIndex + 1;
    this.getList();
  }

  onSortChanged(sortEvent: Sort): void {
    let direction = '';

    if (sortEvent.direction === 'asc') {
      direction = '';
    } else if (sortEvent.direction === 'desc') {
      direction = '-';
    }
    if (sortEvent.direction === '') {
      this.sortValue = null;
    } else {
      this.sortValue = ` ${direction}${sortEvent.active}`;
    }

    this.getList();
  }

  private initSubscriptions(): void {
    const newRequest = new PaginationRequest('', '');
    this.usersSrv.getListWithPagination(newRequest, null, true);

    this.addSubscription(
      this.usersSrv.usersListChanged.subscribe((users) => {
        this.users = users;
        this.initTable();
        this.sortTable();
        this.onEditFinished();
      }),
    );

    this.addSubscription(
      this.usersSrv.paginationSubject.subscribe((pagination) => {
        this.pagination = pagination;
      }),
    );

    this.addSubscription(
      this.filterSubject.pipe(debounceTime(300)).subscribe(() => {
        this.getList();
      }),
    );
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.filter = filterValue.trim().toLowerCase();

    this.filterSubject.next(this.filter);
  }
}
